import { Common } from "../../../common/src/index";
import type { EGroup } from "../../pages/Home";

export type ClusteredProjects = { [name: string]: [Common.Project] };

/** 有効な分類区分。EGroupはconst enumのためランタイムでは参照できず、リテラルで保持する */
const GROUP_SEPS = ["year", "month", "day"] as const;
type GroupSep = (typeof GROUP_SEPS)[number];

/** sepが不正・未指定だった場合のフォールバック */
const DEFAULT_SEP: GroupSep = "day";

// フィルタ処理用
export default class Clusterings {
  static resolveSortDate(project: Common.Project, sortBy?: string): Date {
    if (sortBy === "upload") {
      return project.uploadTime;
    }
    return project.captureTime ?? project.uploadTime;
  }

  static excludeByIds(
    projectList: Common.Project[],
    ids: string[],
  ): Common.Project[] {
    if (!ids || ids.length === 0) {
      return projectList;
    }
    return projectList.filter((project) => !ids.includes(project.id));
  }

  /**
   * sepを有効な分類区分に正規化します。
   * ToggleButtonGroup(exclusive)は選択中のボタン再クリックでnullを返すため、
   * 不正値はthrowせず既定値へフォールバックする(render中のthrowは画面全体を落とす)。
   */
  private static normalizeSep(sep?: EGroup | null): GroupSep {
    const raw = sep as string | null | undefined;
    if (raw && (GROUP_SEPS as readonly string[]).includes(raw)) {
      return raw as GroupSep;
    }
    if (raw != null) {
      console.warn(
        `不正な分類区分です: ${String(raw)}。"${DEFAULT_SEP}"で分類します。`,
      );
    }
    return DEFAULT_SEP;
  }

  private static zeroPadding2(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  /**
   * クラスタキーを生成します。形式は従来通り year:"yyyy" / month:"yyyy/MM" / day:"yyyy/MM/dd"
   * (キーは表示ラベルかつキーソートにも使われるためゼロ埋め必須)
   */
  private static buildClusterKey(date: Date, sep: GroupSep): string {
    const year = date.getFullYear();
    if (sep === "year") {
      return `${year}`;
    }
    const month = Clusterings.zeroPadding2(date.getMonth() + 1);
    if (sep === "month") {
      return `${year}/${month}`;
    }
    return `${year}/${month}/${Clusterings.zeroPadding2(date.getDate())}`;
  }

  /**
   * ルールに基づいてprojectListをクラスタリングします。
   * イメージ {dateName1: [project1-1, project1-2], dateName2: [project2-1, project2-2],...}
   * @param projectList
   * @param sep クラスタリングルール. ["day", "month", "year"]. 不正値・未指定は "day"
   * @param sortBy 現在のソートモード ("create" | "upload"). 対応する日付フィールドでクラスタリングする
   * @returns
   */
  static clusterByDate(
    projectList: Common.Project[],
    sep?: EGroup | null,
    sortBy?: string,
  ): ClusteredProjects {
    const displayProjects: ClusteredProjects = {};
    const normalizedSep = Clusterings.normalizeSep(sep);

    for (const project of projectList) {
      // APIからはISO文字列で届くためDateへ正規化する
      const rawDate = Clusterings.resolveSortDate(project, sortBy) as
        | Date
        | string;
      const date = rawDate instanceof Date ? rawDate : new Date(rawDate);
      const key = Clusterings.buildClusterKey(date, normalizedSep);

      const cluster = displayProjects[key];
      if (cluster) {
        cluster.push(project);
      } else {
        displayProjects[key] = [project];
      }
    }

    return displayProjects;
  }
}
